/**
 * Synonym suggestion rows with Add / Decline (features/synonym_suggestions.md).
 *
 * Shared by the Define Synonyms card in Edit Resume ("Suggested") and the job
 * editor's review dialog. Pure presentation: the caller owns filtering and
 * what Add/Decline actually do (local resume state vs. an immediate save).
 */
import { Check, X, Loader2, Lightbulb } from 'lucide-react';
import type { SynonymSet, SynonymSuggestion } from '../types';
import { describeSuggestion } from '../utils/synonymSuggestions';

interface SynonymSuggestionListProps {
  suggestions: SynonymSuggestion[];
  sets?: SynonymSet[];
  onAdd: (s: SynonymSuggestion) => void;
  onDecline: (s: SynonymSuggestion) => void;
  /** pair_key currently being saved (its buttons show a spinner). */
  busyKey?: string | null;
  /** Show the job(s) each suggestion came from (resume-level aggregate). */
  showJobs?: boolean;
}

function jobLabel(j: { jobtitle: string; jobcompany: string }) {
  return [j.jobtitle, j.jobcompany].filter(Boolean).join(' @ ');
}

export function SynonymSuggestionList({
  suggestions, sets, onAdd, onDecline, busyKey = null, showJobs = false,
}: SynonymSuggestionListProps) {
  return (
    <ul className="space-y-1.5" data-testid="synonym-suggestion-list">
      {suggestions.map(s => {
        const busy = busyKey === s.pair_key;
        const jobs = s.jobs || [];
        return (
          <li
            key={s.pair_key}
            data-testid="synonym-suggestion"
            data-pair-key={s.pair_key}
            className="border border-purple-200 bg-purple-50 rounded px-3 py-2"
          >
            <div className="flex items-start gap-2">
              <Lightbulb className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-purple-900 break-words">
                  <span className="font-medium">{s.resume_term}</span>
                  <span className="text-purple-500"> on your resume ≈ </span>
                  <span className="font-medium">{s.job_term}</span>
                  <span className="text-purple-500"> in the job</span>
                </p>
                {s.target_set_id && (
                  <p className="text-xs text-purple-700 mt-0.5">{describeSuggestion(s, sets)}</p>
                )}
                {showJobs && jobs.length > 0 && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate" title={jobs.map(jobLabel).join('\n')}>
                    From {jobLabel(jobs[0])}
                    {jobs.length > 1 && ` and ${jobs.length - 1} more job${jobs.length > 2 ? 's' : ''}`}
                  </p>
                )}
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => onAdd(s)}
                  disabled={busy}
                  data-testid="synonym-suggestion-add"
                  className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-50"
                  title={s.target_set_id ? 'Add to your existing synonym set' : 'Add as a new synonym set'}
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Add
                </button>
                <button
                  type="button"
                  onClick={() => onDecline(s)}
                  disabled={busy}
                  data-testid="synonym-suggestion-decline"
                  className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50"
                  title="These don't mean the same thing — never suggest this pair again"
                >
                  <X className="w-3 h-3" /> Decline
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
